<template>
  <div class="users-tab-shell">
    <!-- Collapsible Edit Panel -->
    <transition name="slide">
      <div v-if="showForm" class="user-form-panel">
        <div class="form">
          <div class="row">
            <div class="col-12">
              <h4 style="margin: 0 0 0.75rem 0;">{{ isEditing ? $t('partner_users.edit_user') : $t('partner_users.new_user') }}</h4>
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-4">
              <ZSInput :label="$t('partner_users.login')" v-model="userForm.login" />
            </div>
            <div class="col-12 col-md-8">
              <ZSInput :label="$t('partner_users.nome')" v-model="userForm.nome" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <ZSInput @blur="checkEmail" :label="$t('partner_users.email')" v-model="userForm.email" />
            </div>
          </div>
          <div class="row">
            <div class="col-12" style="display: flex; align-items: center; padding-top: 0.25rem;">
              <label style="margin-right: 0.75rem;">{{ $t('partner_users.blocked') }}</label>
              <ZSSwitch v-model="userForm.bloqueado" :trueValue="1" :falseValue="0" color="danger" :disabled="editingUserId === userStore.user.user_id" />
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-6">
              <ZSInput :label="$t('partner_users.password')" type="password" v-model="userForm.password" />
            </div>
            <div class="col-12 col-md-6">
              <ZSInput :label="$t('partner_users.confirm_password')" type="password" v-model="userForm.confirmPassword" />
            </div>
          </div>
          <div class="row">
            <div class="col-12" style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.75rem;">
              <ZSButton class="text" color="danger" @click="cancelForm">{{ $t('partner_users.cancel') }}</ZSButton>
              <ZSButton color="primary" @click="saveUser">{{ $t('partner_users.save') }}</ZSButton>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toolbar -->
    <div style="display: flex; justify-content: flex-end; margin-bottom: 0.75rem;">
      <ZSButton color="primary" icon="Plus" @click="openCreateForm" v-if="!showForm">
        {{ $t('partner_users.new_user') }}
      </ZSButton>
    </div>

    <!-- Users Grid -->
    <ZSGrid
      :data="users"
      :cols="columns"
      :paginator="true"
      :totalItems="totalRecords"
      :current-page="currentPage"
      item-key="codigo"
      :busy="loading"
      @page-changed="onPageChanged"
      @filter="onFilter"
    >
      <template #nome="{ item }">
        <div class="user-name-cell">
          <span>{{ item.nome }}</span>
          <span v-if="isMasterUser(item)" class="master-user-badge" v-tooltip:top="'chef'">
            <ZSIcon icon="userStars" size="small" />
          </span>
        </div>
      </template>
      <template #bloqueado="{ item }">
        <ZSChip :label="item.bloqueado ? $t('partner_users.blocked') : $t('partner_users.active')" :type="item.bloqueado ? 'danger' : 'success'" />
      </template>
      <template #roles="{ item }">
        <div class="user-roles-list">
          <ZSChip v-for="role in getRoleNamesForUser(item)" :key="`${item.codigo}-${role}`" :label="role" type="info" />
          <span v-if="getRoleNamesForUser(item).length === 0" class="user-roles-empty">-</span>
        </div>
      </template>
      <template #options="{ item }">
        <div class="zs-options-holder">
          <button class="options-button" @click="openEditForm(item)" v-tooltip:top="$t('partner_users.edit_user')">
            <ZSIcon icon="Pencil" />
          </button>
          <button v-if="canManageRoles" class="options-button" @click="openRoleDialog(item)" v-tooltip:top="$t('system.rbac.assign_roles')">
            <ZSIcon icon="userSettings" />
          </button>
          <button class="options-button" @click="toggleBlock(item)" v-tooltip:top="item.bloqueado ? $t('partner_users.confirm_unblock') : $t('partner_users.confirm_block')">
            <ZSIcon :icon="item.bloqueado ? 'Unlock' : 'Lock'" />
          </button>
          <button class="options-button" @click="confirmDelete(item)" v-tooltip:top="$t('partner_users.confirm_delete')">
            <ZSIcon icon="Trash" />
          </button>
        </div>
      </template>
    </ZSGrid>

    <ZSDialog ref="zsDialogRef" type="confirm" />
    <ZSDialog ref="userRolesDialogRef" :title="$t('system.rbac.assign_roles')" width="min(760px, 100vw)" height="min(420px, 85vh)" hideButtons>
      <div class="role-dialog-form">
        <div v-if="roleDialogBusy" class="role-dialog-overlay">
          <Loader />
        </div>
        <div class="role-dialog-info">{{ t('system.rbac.assign_roles_to_user', { name: selectedUser?.nome || selectedUser?.login || '' }) }}</div>
        <div class="role-dialog-hint">{{ t('system.rbac.single_role_hint') }}</div>
        <ZSSelect
          class="role-dialog-field"
          :label="t('system.rbac.role_to_assign')"
          v-model="selectedUserRoleId"
          :items="roleRows"
          itemLabel="name"
          itemValue="id"
          clear
          :disabled="roleDialogBusy"
        />
        <div class="role-dialog-actions">
          <ZSButton class="text" color="danger" :disabled="roleDialogBusy" @click="closeRoleDialog">{{ t('general.cancel') }}</ZSButton>
          <ZSButton color="orange" :disabled="roleDialogBusy" @click="saveUserRoles">{{ t('general.save') }}</ZSButton>
        </div>
      </div>
    </ZSDialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRBACStore } from '@/stores/rbac';
import { useUserStore } from "@/stores/user";
import { useZSToast } from "@/components/zs-components/toast/useToast";
import { useZSDialogs } from "@/composables/notifications";
import UserService from "@/services/client";
import Loader from '@/components/zs-components/data/Loader.vue';

const { t } = useI18n();
const rbacStore = useRBACStore();
const userStore = useUserStore();
const { add: addToast } = useZSToast();

const zsDialogRef = ref(null);
const userRolesDialogRef = ref(null);
const { showConfirm } = useZSDialogs(zsDialogRef);

const us = new UserService();

const users = ref([]);
const totalRecords = ref(0);
const currentPage = ref(1);
const perPage = 10;
const loading = ref(false);
const prevFilter = ref({});
const selectedUser = ref(null);
const selectedUserRoleId = ref(null);
const roleDialogBusy = ref(false);

// Form state
const showForm = ref(false);
const isEditing = ref(false);
const editingUserId = ref(null);
const userForm = ref({
  login: '',
  nome: '',
  email: '',
  password: '',
  confirmPassword: '',
  bloqueado: 0
});

const columns = computed(() => [
  { field: 'nome', label: t('partner_users.nome'), filter: { type: 'string', align: 'left' }, style: { width: '20%' } },
  { field: 'login', label: t('partner_users.login'), filter: { type: 'string', align: 'left' }, style: { width: '18%' } },
  { field: 'email', label: t('partner_users.email'), filter: { type: 'string', align: 'left' }, style: { width: '24%' } },
  { field: 'roles', label: t('system.rbac.roles_title'), style: { width: '18%' } },
  { field: 'bloqueado', label: t('partner_users.state'), style: { width: '8%' } },
  { field: 'options', label: t('partner_users.options'), style: { width: '12%' } },
]);
const canManageRoles = computed(() => userStore.user?.master === true || (userStore.rbacEnabled && userStore.permissions.includes('partner.permissions.view')));
const roleRows = computed(() => rbacStore.roles);

const fetchUsers = async (filters = {}, page = 1) => {
  loading.value = true;
  try {
    const data = await us.getPartnerUsers(userStore.token, page, perPage, filters);
    users.value = data?.data ?? [];
    totalRecords.value = data?.meta?.total_items ?? 0;
  } catch (e) {
    // Error handled by axios interceptor
  } finally {
    loading.value = false;
  }
};

const onPageChanged = (pageData) => {
  const page = typeof pageData === 'object' ? pageData.page : pageData;
  currentPage.value = page;
  fetchUsers(prevFilter.value, page);
};

const onFilter = (newFilter) => {
  const filterObj = Object.entries(newFilter).reduce((acc, [key, value]) => {
    if (value?.value) {
      let k = key;
      if (newFilter[k]?.column?.alias) k = newFilter[k].column.alias;
      acc[k] = value.value;
    }
    return acc;
  }, {});
  prevFilter.value = filterObj;
  currentPage.value = 1;
  fetchUsers(filterObj, 1);
};

const resetForm = () => {
  userForm.value = { login: '', nome: '', email: '', password: '', confirmPassword: '', bloqueado: 0 };
  isEditing.value = false;
  editingUserId.value = null;
};

const openCreateForm = () => {
  resetForm();
  showForm.value = true;
};

const openEditForm = (item) => {
  resetForm();
  isEditing.value = true;
  editingUserId.value = item.codigo;
  userForm.value.login = item.login || '';
  userForm.value.nome = item.nome || '';
  userForm.value.email = item.email || '';
  userForm.value.bloqueado = item.bloqueado ?? 0;
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  resetForm();
};

const getRoleIDForUser = (user) => {
  const rbacUser = rbacStore.users.find((candidate) => candidate.codigo === user.codigo);
  const assignedRoles = Array.isArray(rbacUser?.roles) ? rbacUser.roles : [];

  return roleRows.value
    .filter((role) => assignedRoles.includes(role.name))
    .map((role) => role.id)[0] || null;
};

const getRoleNamesForUser = (user) => {
  const rbacUser = rbacStore.users.find((candidate) => candidate.codigo === user.codigo);
  return Array.isArray(rbacUser?.roles) ? rbacUser.roles : [];
};

const isMasterUser = (user) => {
  return Number(rbacStore.settings?.master_id ?? 0) === Number(user?.codigo ?? 0);
};

const openRoleDialog = async (item) => {
  if (!canManageRoles.value) {
    return;
  }

  roleDialogBusy.value = true;

  try {
    if (roleRows.value.length === 0 || !rbacStore.users.some((user) => user.codigo === item.codigo)) {
      await rbacStore.refreshRolesAndUsers();
    }

    selectedUser.value = item;
    selectedUserRoleId.value = getRoleIDForUser(item);
    userRolesDialogRef.value?.show();
  } finally {
    roleDialogBusy.value = false;
  }
};

const closeRoleDialog = (force = false) => {
  if (roleDialogBusy.value && !force) {
    return;
  }

  userRolesDialogRef.value?.hide();
  selectedUser.value = null;
  selectedUserRoleId.value = null;
};

const saveUserRoles = async () => {
  if (!selectedUser.value) {
    return;
  }

  roleDialogBusy.value = true;

  try {
    await rbacStore.saveUserRoles(selectedUser.value.codigo, selectedUserRoleId.value ? [selectedUserRoleId.value] : []);
    closeRoleDialog(true);
  } finally {
    roleDialogBusy.value = false;
  }
};

const saveUser = async () => {
  if (!userForm.value.login || !userForm.value.nome || !userForm.value.email) {
    addToast({ position: 'tr', summary: t('partner_users.edit_user'), detail: t('partner_users.required_fields'), type: 'warn', life: 4000 });
    return;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.value.email)) {
    addToast({ position: 'tr', summary: t('partner_users.edit_user'), detail: t('validation.email'), type: 'warn', life: 4000 });
    return;
  }

  if (userForm.value.password && userForm.value.password !== userForm.value.confirmPassword) {
    addToast({ position: 'tr', summary: t('partner_users.edit_user'), detail: t('partner_users.password_mismatch'), type: 'warn', life: 4000 });
    return;
  }

  const payload = {
    login: userForm.value.login,
    nome: userForm.value.nome,
    email: userForm.value.email,
    bloqueado: userForm.value.bloqueado,
  };

  if (userForm.value.password) {
    payload.password = userForm.value.password;
  }

  try {
    if (isEditing.value) {
      await us.updatePartnerUser(userStore.token, editingUserId.value, payload);
      addToast({ position: 'tr', summary: t('partner_users.edit_user'), detail: t('partner_users.user_updated'), type: 'success', life: 4000 });
    } else {
      if (!userForm.value.password) {
        addToast({ position: 'tr', summary: t('partner_users.new_user'), detail: t('partner_users.password_fields_required'), type: 'warn', life: 4000 });
        return;
      }
      payload.password = userForm.value.password;
      await us.createPartnerUser(userStore.token, payload);
      addToast({ position: 'tr', summary: t('partner_users.new_user'), detail: t('partner_users.user_created'), type: 'success', life: 4000 });
    }
    showForm.value = false;
    resetForm();
    fetchUsers(prevFilter.value, currentPage.value);
  } catch (e) {
    // Email duplicado: o backend devolve 409 com code partner.user.email_already_exists
    if (e?.response?.status === 409 || e?.response?.data?.code === 'partner.user.email_already_exists') {
      addToast({ position: 'tr', summary: isEditing.value ? t('partner_users.edit_user') : t('partner_users.new_user'), detail: t('partner_users.email_already_exists'), type: 'warn', life: 4000 });
      return;
    }
    // Restantes erros tratados pelo interceptor axios
  }
};

const toggleBlock = async (item) => {
  const newBloqueado = item.bloqueado ? 0 : 1;
  const confirmMsg = item.bloqueado ? t('partner_users.confirm_unblock') : t('partner_users.confirm_block');

  showConfirm(
    t('partner_users.state'),
    confirmMsg,
    async () => {
      try {
        await us.updatePartnerUser(userStore.token, item.codigo, {
          login: item.login,
          nome: item.nome,
          email: item.email || '',
          bloqueado: newBloqueado,
        });
        addToast({ position: 'tr', summary: t('partner_users.state'), detail: t('partner_users.user_updated'), type: 'success', life: 4000 });
        fetchUsers(prevFilter.value, currentPage.value);
      } catch (e) {
        // Error handled by axios interceptor
      }
    }
  );
};

const confirmDelete = (item) => {
  showConfirm(
    t('partner_users.confirm_delete'),
    t('partner_users.confirm_delete'),
    async () => {
      try {
        await us.deletePartnerUser(userStore.token, item.codigo);
        addToast({ position: 'tr', summary: t('partner_users.confirm_delete'), detail: t('partner_users.user_deleted'), type: 'success', life: 4000 });
        fetchUsers(prevFilter.value, currentPage.value);
      } catch (e) {
        // Error handled by axios interceptor
      }
    }
  );
};


const  checkEmail = async (val) => {
  if(!val)return
   const res = await us.checkEmailExists(userStore.token,val)
   const userID =  (res?.data??0)
   if(userID > 0 && userID != editingUserId.value ) {
    addToast({ position: 'tr', summary: t('partner_users.email_already_exists'), type: 'warn', life: 4000 });
   }
}



onMounted(() => {
  fetchUsers();
  rbacStore.refreshSettings();
});
</script>

<style scoped>
.users-tab-shell {
  width: 100%;
}

.user-form-panel {
  background: var(--zs-surface-color, #f8f9fa);
  border: 1px solid var(--zs-border-color, #e0e0e0);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding: 0 1rem;
  margin-bottom: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 500px;
  opacity: 1;
}

.zs-options-holder {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.role-dialog-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 280px;
  position: relative;
}

.role-dialog-info {
  color: var(--color-dark-blue);
  font-size: 0.95rem;
}

.role-dialog-hint {
  color: var(--color-gray-icon);
  font-size: 0.9rem;
}

.role-dialog-field {
  position: relative;
  z-index: 2;
}

.role-dialog-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1rem;
  position: sticky;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff 20px);
}

.role-dialog-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.78);
  z-index: 8;
  border-radius: 10px;
}

.options-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.options-button:hover {
  background: var(--zs-hover-color, #e9ecef);
}

.user-roles-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.user-roles-empty {
  color: var(--color-gray-icon);
}

.user-name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.master-user-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--color-orange);
  font-size: 0.85rem;
  font-weight: 600;
}
</style>
